const LEGACY_PARENT_DOMAIN = ".yourbarrio.com";
const LEGACY_SUPABASE_COOKIE_NAMES = new Set([
  "supabase-auth-token",
  "supabase.auth.token",
]);

export const SUPABASE_COOKIE_MAX_AGE = 400 * 24 * 60 * 60;
export const AUTH_COOKIE_SCOPE = "host-only";
export const AUTH_COOKIE_SCOPE_VERSION = "host-only-v2";
export const AUTH_COOKIE_SCOPE_MARKER = "yb_auth_cookie_scope";
export const AUTH_LOGOUT_INTENT_COOKIE = "yb_auth_logout_intent";

const COOKIE_NAME_PATTERN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
const EXPLICIT_AUXILIARY_COOKIE_NAMES = new Set([
  "yb:returnTo",
  "yb:postLoginRedirect",
]);
const SUPABASE_AUTH_COOKIE_PATTERN = /^(sb-.+-auth-token)(?:\.(\d+))?$/;
const SUPABASE_AUTH_COOKIE_CHUNK_COUNT = 5;

function isSerializableCookieName(name) {
  const normalizedName = String(name || "");
  return (
    COOKIE_NAME_PATTERN.test(normalizedName) ||
    EXPLICIT_AUXILIARY_COOKIE_NAMES.has(normalizedName)
  );
}

function firstHeaderValue(value) {
  return String(value || "")
    .split(",")[0]
    .trim();
}

export function getCookieHostname(host) {
  const input = String(host || "").trim().toLowerCase();
  if (!input) return "";

  if (input.includes("://")) {
    try {
      return new URL(input).hostname.toLowerCase();
    } catch {
      return "";
    }
  }

  if (input.startsWith("[")) {
    const closingBracket = input.indexOf("]");
    return closingBracket === -1 ? input : input.slice(1, closingBracket);
  }

  return input.split(":")[0];
}

export function isLocalCookieHost(host) {
  const hostname = getCookieHostname(host);
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname.endsWith(".localhost")
  );
}

export function isYourBarrioCookieHost(host) {
  const hostname = getCookieHostname(host);
  return hostname === "yourbarrio.com" || hostname.endsWith(".yourbarrio.com");
}

// Retained for callers that previously inspected the server cookie domain.
// Phase 2 deliberately uses a host-only contract, so there is no canonical
// Domain attribute.
export function getCanonicalCookieDomain() {
  return undefined;
}

export function getCookieBaseOptions({ host, isProd, protocol } = {}) {
  const normalizedProtocol = String(protocol || "").trim().toLowerCase();
  const secure =
    normalizedProtocol === "https:" ||
    normalizedProtocol === "https" ||
    Boolean(isProd && !isLocalCookieHost(host));

  return {
    sameSite: "lax",
    secure,
    path: "/",
    httpOnly: false,
  };
}

export function getRequestCookieBaseOptions(request) {
  const requestUrl = request?.nextUrl || (() => {
    try {
      return new URL(request?.url || "");
    } catch {
      return null;
    }
  })();
  const host =
    firstHeaderValue(request?.headers?.get?.("x-forwarded-host")) ||
    firstHeaderValue(request?.headers?.get?.("host")) ||
    requestUrl?.host ||
    "";
  const protocol = isLocalCookieHost(host)
    ? requestUrl?.protocol || ""
    : firstHeaderValue(request?.headers?.get?.("x-forwarded-proto")) ||
      requestUrl?.protocol ||
      "";

  return getCookieBaseOptions({
    host,
    protocol,
    isProd: process.env.NODE_ENV === "production",
  });
}

export function getBrowserCookieBaseOptions() {
  if (typeof window === "undefined") {
    return getCookieBaseOptions({
      host: "",
      protocol: "",
      isProd: process.env.NODE_ENV === "production",
    });
  }
  return getCookieBaseOptions({
    host: window.location.host,
    protocol: window.location.protocol,
    isProd: process.env.NODE_ENV === "production",
  });
}

function getCookieHeader(req) {
  if (!req?.headers) return "";
  if (typeof req.headers.get === "function") {
    return req.headers.get("cookie") || "";
  }
  return req.headers.cookie || "";
}

function decodeCookieValue(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function parseCookieHeaderEntries(cookieHeader) {
  const entries = [];
  String(cookieHeader || "")
    .split(";")
    .forEach((pair, index) => {
      const trimmed = pair.trim();
      if (!trimmed) return;
      const eqIndex = trimmed.indexOf("=");
      if (eqIndex <= 0) return;
      const name = trimmed.slice(0, eqIndex).trim();
      if (!COOKIE_NAME_PATTERN.test(name)) return;
      entries.push({
        name,
        value: decodeCookieValue(trimmed.slice(eqIndex + 1)),
        index,
      });
    });
  return entries;
}

function getRequestCookieEntries(req) {
  const rawEntries = parseCookieHeaderEntries(getCookieHeader(req));
  if (rawEntries.length) return rawEntries;
  if (req?.cookies && typeof req.cookies.getAll === "function") {
    return req.cookies.getAll().map((cookie, index) => ({
      name: cookie.name,
      value: cookie.value,
      index,
    }));
  }
  return [];
}

export function isSupabaseAuthCookieName(name) {
  return SUPABASE_AUTH_COOKIE_PATTERN.test(String(name || ""));
}

export function isLegacySupabaseCookieName(name) {
  const normalizedName = String(name || "");
  return (
    normalizedName.startsWith("__Host-sb-") ||
    LEGACY_SUPABASE_COOKIE_NAMES.has(normalizedName)
  );
}

export function getSupabaseAuthCookieRoot(name) {
  return String(name || "").match(SUPABASE_AUTH_COOKIE_PATTERN)?.[1] || null;
}

export function getSupabaseAuthCookieName(supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL) {
  try {
    const projectRef = new URL(String(supabaseUrl || "")).hostname.split(".")[0];
    return projectRef ? `sb-${projectRef}-auth-token` : undefined;
  } catch {
    return undefined;
  }
}

export function getSbCookieNamesFromRequest(req) {
  return Array.from(
    new Set(
      getRequestCookieEntries(req)
        .filter((cookie) => cookie.name.startsWith("sb-"))
        .map((cookie) => cookie.name)
    )
  );
}

export function getKnownSupabaseCookieNamesFromRequest(req) {
  return Array.from(
    new Set(
      getRequestCookieEntries(req)
        .filter(
          ({ name }) =>
            name.startsWith("sb-") || isLegacySupabaseCookieName(name)
        )
        .map(({ name }) => name)
    )
  );
}

export function getSupabaseAuthCookieNamesFromRequest(req) {
  return getSbCookieNamesFromRequest(req).filter(isSupabaseAuthCookieName);
}

/**
 * Returns every auth-cookie name Supabase SSR may read for the current project.
 * This is used only to expire a legacy path: cookies scoped to that path are
 * invisible from /auth, so request-visible names alone cannot remove a stale
 * base-versus-chunks generation.
 */
export function getSupabaseAuthCookieNamesForPathCleanup(req) {
  const expectedCookieName = getSupabaseAuthCookieName();
  const roots = new Set(
    getSupabaseAuthCookieNamesFromRequest(req)
      .map(getSupabaseAuthCookieRoot)
      .filter(Boolean),
  );
  if (expectedCookieName) roots.add(expectedCookieName);

  return Array.from(roots).flatMap((root) => [
    root,
    ...Array.from(
      { length: SUPABASE_AUTH_COOKIE_CHUNK_COUNT },
      (_, index) => `${root}.${index}`,
    ),
  ]);
}

export function clearBrowserSupabaseAdminPathCookies() {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  const baseOptions = getBrowserCookieBaseOptions();
  const browserRequest = {
    headers: {
      get(name) {
        return String(name || "").toLowerCase() === "cookie"
          ? document.cookie
          : null;
      },
    },
  };
  const names = getSupabaseAuthCookieNamesForPathCleanup(browserRequest);

  names.forEach((name) => {
    document.cookie = serializeCookieHeader(name, "", {
      ...baseOptions,
      path: "/admin",
      maxAge: 0,
      expires: new Date(0),
    });
  });

  if (getCookieHostname(window.location.host).endsWith(".yourbarrio.com")) {
    names.forEach((name) => {
      document.cookie = serializeCookieHeader(name, "", {
        ...baseOptions,
        domain: LEGACY_PARENT_DOMAIN,
        path: "/admin",
        maxAge: 0,
        expires: new Date(0),
      });
    });
  }
}

export function findDuplicateSbCookieNames(req) {
  const counts = new Map();
  getRequestCookieEntries(req).forEach(({ name }) => {
    if (!name.startsWith("sb-")) return;
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  return Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .map(([name]) => name);
}

function uniqueEntriesByName(entries) {
  const seen = new Set();
  return entries.filter(({ name }) => {
    if (seen.has(name)) return false;
    seen.add(name);
    return true;
  });
}

/**
 * Builds coherent candidate generations from the raw Cookie header without
 * logging or decoding Supabase token contents. This covers same-name
 * host/parent duplicates and the base-cookie-versus-chunks migration shape.
 */
export function getSupabaseCookieCandidates(req, { cookieName } = {}) {
  const entries = getRequestCookieEntries(req).filter(({ name }) =>
    isSupabaseAuthCookieName(name)
  );
  if (!entries.length) return [];

  const requestedRoot = cookieName || getSupabaseAuthCookieName();
  const discoveredRoots = Array.from(
    new Set(entries.map(({ name }) => getSupabaseAuthCookieRoot(name)).filter(Boolean))
  );
  const root =
    (requestedRoot && discoveredRoots.includes(requestedRoot) && requestedRoot) ||
    discoveredRoots[0] ||
    null;

  if (!root) {
    const maxCount = Math.max(
      ...Array.from(
        entries.reduce((counts, entry) => {
          counts.set(entry.name, (counts.get(entry.name) || 0) + 1);
          return counts;
        }, new Map()).values()
      )
    );
    return Array.from({ length: maxCount }, (_, occurrence) =>
      uniqueEntriesByName(
        entries.filter((entry, index, all) => {
          const sameName = all.filter(({ name }) => name === entry.name);
          return sameName[Math.min(occurrence, sameName.length - 1)] === entry;
        })
      )
        .sort((a, b) => a.index - b.index)
        .map(({ name, value }) => ({ name, value }))
    );
  }

  const sessionEntries = entries.filter(({ name }) => getSupabaseAuthCookieRoot(name) === root);
  const baseEntries = sessionEntries.filter(({ name }) => name === root);
  const chunkEntriesByName = new Map();

  sessionEntries
    .filter(({ name }) => name !== root)
    .forEach((entry) => {
      const values = chunkEntriesByName.get(entry.name) || [];
      values.push(entry);
      chunkEntriesByName.set(entry.name, values);
    });

  const variants = baseEntries.map((entry) => ({
    entries: [entry],
    firstIndex: entry.index,
  }));
  const maxChunkOccurrences = Math.max(
    0,
    ...Array.from(chunkEntriesByName.values()).map((values) => values.length)
  );

  const sortedChunkEntries = Array.from(chunkEntriesByName.entries()).sort(
    ([a], [b]) => {
      const aIndex = Number(a.slice(root.length + 1));
      const bIndex = Number(b.slice(root.length + 1));
      return aIndex - bIndex;
    }
  );

  for (let occurrence = 0; occurrence < maxChunkOccurrences; occurrence += 1) {
    const chunkEntries = sortedChunkEntries
      .map(([, values]) => values[occurrence])
      .filter(Boolean);
    if (chunkEntries.length === sortedChunkEntries.length) {
      variants.push({
        entries: chunkEntries,
        firstIndex: Math.min(...chunkEntries.map(({ index }) => index)),
      });
    }
  }

  // Cookie headers do not expose Domain metadata. When one legacy scope has a
  // partial chunk set, occurrence-based grouping can splice scopes together.
  // Add bounded combinations so the complete valid generation is still tried.
  const chunkOccurrenceCounts = sortedChunkEntries.map(
    ([, values]) => values.length
  );
  if (new Set(chunkOccurrenceCounts).size > 1) {
    const chunkCombinations = [];
    const appendChunkCombinations = (chunkIndex, current) => {
      if (chunkCombinations.length >= 32) return;
      if (chunkIndex >= sortedChunkEntries.length) {
        chunkCombinations.push(current);
        return;
      }
      const [, values] = sortedChunkEntries[chunkIndex];
      values.forEach((entry) => {
        if (chunkCombinations.length >= 32) return;
        appendChunkCombinations(chunkIndex + 1, [...current, entry]);
      });
    };
    appendChunkCombinations(0, []);
    chunkCombinations.forEach((chunkEntries) => {
      if (!chunkEntries.length) return;
      variants.push({
        entries: chunkEntries,
        firstIndex: Math.min(...chunkEntries.map(({ index }) => index)),
      });
    });
  }

  if (!variants.length) {
    return [
      uniqueEntriesByName(entries)
        .sort((a, b) => a.index - b.index)
        .map(({ name, value }) => ({ name, value })),
    ];
  }

  const seenVariants = new Set();
  return variants
    .sort((a, b) => a.firstIndex - b.firstIndex)
    .map((variant) =>
      variant.entries
        .sort((a, b) => a.index - b.index)
        .map(({ name, value }) => ({ name, value }))
    )
    .filter((candidate) => {
      const signature = candidate
        .map(({ name, value }) => `${name.length}:${name}${value.length}:${value}`)
        .join("|");
      if (seenVariants.has(signature)) return false;
      seenVariants.add(signature);
      return true;
    });
}

function decodeBase64UrlUtf8(value) {
  const input = String(value || "").replace(/-/g, "+").replace(/_/g, "/");
  const padded = input.padEnd(
    input.length + ((4 - (input.length % 4)) % 4),
    "="
  );
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) =>
    character.charCodeAt(0)
  );
  return new TextDecoder().decode(bytes);
}

function parseJsonValue(value) {
  const rawValue = String(value || "");
  const valuesToTry = [rawValue];
  try {
    const decodedValue = decodeURIComponent(rawValue);
    if (decodedValue !== rawValue) valuesToTry.push(decodedValue);
  } catch {
    // The Cookie header parser may already have decoded the value.
  }

  for (const candidateValue of valuesToTry) {
    let jsonValue = candidateValue;
    if (jsonValue.startsWith("base64-")) {
      try {
        jsonValue = decodeBase64UrlUtf8(
          jsonValue.slice("base64-".length)
        );
      } catch {
        continue;
      }
    }
    try {
      return JSON.parse(jsonValue);
    } catch {
      // Try the next representation.
    }
  }
  return null;
}

function getCandidateSerializedSession(candidate) {
  if (!Array.isArray(candidate) || !candidate.length) return null;
  const root = candidate
    .map(({ name }) => getSupabaseAuthCookieRoot(name))
    .find(Boolean);
  if (!root) return null;

  const baseCookie = candidate.find(({ name }) => name === root);
  if (baseCookie?.value) return baseCookie.value;

  const chunks = candidate
    .filter(({ name }) => getSupabaseAuthCookieRoot(name) === root)
    .map((cookie) => ({
      ...cookie,
      index: Number(String(cookie.name).slice(root.length + 1)),
    }))
    .filter(({ index }) => Number.isInteger(index) && index >= 0)
    .sort((left, right) => left.index - right.index);
  if (
    !chunks.length ||
    chunks.some(({ index }, position) => index !== position)
  ) {
    return null;
  }
  return chunks.map(({ value }) => value || "").join("");
}

function finiteTimestamp(value) {
  const timestamp = Number(value);
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : null;
}

function parseAccessTokenTimestamps(accessToken) {
  const segments = String(accessToken || "").split(".");
  if (segments.length !== 3) {
    return { issuedAt: null, accessTokenExpiresAt: null };
  }
  try {
    const payload = JSON.parse(decodeBase64UrlUtf8(segments[1]));
    return {
      issuedAt: finiteTimestamp(payload?.iat),
      accessTokenExpiresAt: finiteTimestamp(payload?.exp),
    };
  } catch {
    return { issuedAt: null, accessTokenExpiresAt: null };
  }
}

/**
 * Reads only the serialized Supabase session envelope. Refresh-token contents
 * are never decoded or logged.
 */
export function getSupabaseCookieCandidateSession(candidate) {
  const serializedSession = getCandidateSerializedSession(candidate);
  const parsed = parseJsonValue(serializedSession);
  const accessToken = Array.isArray(parsed)
    ? parsed[0] || null
    : parsed?.access_token || null;
  const refreshToken = Array.isArray(parsed)
    ? parsed[1] || null
    : parsed?.refresh_token || null;
  if (!accessToken || !refreshToken) return null;

  return {
    accessToken,
    refreshToken,
    expiresAt: finiteTimestamp(parsed?.expires_at),
  };
}

function compareSessionRecency(left, right) {
  const leftScore = [
    left.issuedAt ?? Number.NEGATIVE_INFINITY,
    left.accessTokenExpiresAt ?? Number.NEGATIVE_INFINITY,
    left.expiresAt ?? Number.NEGATIVE_INFINITY,
  ];
  const rightScore = [
    right.issuedAt ?? Number.NEGATIVE_INFINITY,
    right.accessTokenExpiresAt ?? Number.NEGATIVE_INFINITY,
    right.expiresAt ?? Number.NEGATIVE_INFINITY,
  ];
  for (let index = 0; index < leftScore.length; index += 1) {
    if (leftScore[index] !== rightScore[index]) {
      return rightScore[index] - leftScore[index];
    }
  }
  return 0;
}

/**
 * Selects one logical newest generation without relying on raw Cookie order.
 * An exact recency tie is left unresolved because Cookie headers do not expose
 * which same-name value came from the host-only versus parent-domain scope.
 */
export function selectNewestSupabaseCookieCandidate(candidates) {
  const parsedCandidates = (Array.isArray(candidates) ? candidates : [])
    .map((candidate) => ({
      candidate,
      session: getSupabaseCookieCandidateSession(candidate),
    }))
    .filter(({ session }) => Boolean(session));

  const logicalGenerations = [];
  const generationKeys = new Set();
  parsedCandidates.forEach((entry) => {
    const generationKey = `${entry.session.accessToken.length}:${
      entry.session.accessToken
    }${entry.session.refreshToken.length}:${entry.session.refreshToken}`;
    if (generationKeys.has(generationKey)) return;
    generationKeys.add(generationKey);
    logicalGenerations.push(entry);
  });

  if (!logicalGenerations.length) {
    return {
      candidate: null,
      session: null,
      ambiguous: false,
      parseableCandidateCount: 0,
    };
  }
  if (logicalGenerations.length === 1) {
    return {
      ...logicalGenerations[0],
      ambiguous: false,
      parseableCandidateCount: parsedCandidates.length,
    };
  }

  // JWT claims are decoded only to rank generations. The selected access token
  // is still validated by the Supabase Auth authority before use.
  const recencyContenders = logicalGenerations.map((entry) => ({
    ...entry,
    session: {
      ...entry.session,
      ...parseAccessTokenTimestamps(entry.session.accessToken),
    },
  }));
  const candidatesWithJwtRecency = recencyContenders.filter(
    ({ session }) =>
      session.issuedAt !== null && session.accessTokenExpiresAt !== null
  );
  if (
    candidatesWithJwtRecency.length > 0 &&
    candidatesWithJwtRecency.length !== recencyContenders.length
  ) {
    return {
      candidate: null,
      session: null,
      ambiguous: true,
      parseableCandidateCount: parsedCandidates.length,
    };
  }

  recencyContenders.sort((left, right) =>
    compareSessionRecency(left.session, right.session)
  );
  if (
    recencyContenders.length > 1 &&
    compareSessionRecency(
      recencyContenders[0].session,
      recencyContenders[1].session
    ) === 0
  ) {
    return {
      candidate: null,
      session: null,
      ambiguous: true,
      parseableCandidateCount: parsedCandidates.length,
    };
  }

  return {
    ...recencyContenders[0],
    ambiguous: false,
    parseableCandidateCount: parsedCandidates.length,
  };
}

export function hasCurrentAuthCookieScopeMarker(req) {
  return getRequestCookieEntries(req).some(
    ({ name, value }) =>
      name === AUTH_COOKIE_SCOPE_MARKER && value === AUTH_COOKIE_SCOPE_VERSION
  );
}

export function shouldNormalizeSupabaseCookieScope(req) {
  return (
    !hasCurrentAuthCookieScopeMarker(req) ||
    findDuplicateSbCookieNames(req).length > 0 ||
    getSupabaseCookieCandidates(req).length > 1
  );
}

function normalizeSameSite(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "strict") return "Strict";
  if (normalized === "none") return "None";
  return "Lax";
}

export function serializeCookieHeader(name, value, options = {}) {
  if (!isSerializableCookieName(name)) {
    throw new TypeError("Invalid cookie name");
  }
  const parts = [`${name}=${encodeURIComponent(String(value ?? ""))}`];
  if (Number.isFinite(options.maxAge)) {
    parts.push(`Max-Age=${Math.floor(options.maxAge)}`);
  }
  if (options.domain) parts.push(`Domain=${options.domain}`);
  if (options.path) parts.push(`Path=${options.path}`);
  if (options.expires instanceof Date) parts.push(`Expires=${options.expires.toUTCString()}`);
  if (options.httpOnly) parts.push("HttpOnly");
  if (options.secure) parts.push("Secure");
  if (options.sameSite) parts.push(`SameSite=${normalizeSameSite(options.sameSite)}`);
  return parts.join("; ");
}

export function getSetCookieHeaders(response) {
  if (!response?.headers) return [];
  if (typeof response.headers.getSetCookie === "function") {
    return response.headers.getSetCookie();
  }
  const combined = response.headers.get("set-cookie");
  if (!combined) return [];
  return combined.split(/,(?=\s*[!#$%&'*+\-.^_`|~0-9A-Za-z]+=)/g);
}

function appendDistinctSetCookieHeader(response, header) {
  if (!response?.headers || !header) return false;
  if (getSetCookieHeaders(response).includes(header)) return false;
  response.headers.append("Set-Cookie", header);
  return true;
}

function getRequestHost(req) {
  return (
    firstHeaderValue(req?.headers?.get?.("x-forwarded-host")) ||
    firstHeaderValue(req?.headers?.get?.("host")) ||
    req?.nextUrl?.host ||
    (() => {
      try {
        return new URL(req?.url || "").host;
      } catch {
        return "";
      }
    })()
  );
}

export function getCanonicalAuthHostRedirectUrl(req) {
  if (getCookieHostname(getRequestHost(req)) !== "yourbarrio.com") {
    return null;
  }
  try {
    const canonicalUrl = new URL(req?.url || "");
    canonicalUrl.protocol = "https:";
    canonicalUrl.hostname = "www.yourbarrio.com";
    canonicalUrl.port = "";
    return canonicalUrl;
  } catch {
    return null;
  }
}

function getLegacyDomainsForRequest(req) {
  const hostname = getCookieHostname(getRequestHost(req));
  return hostname.endsWith(".yourbarrio.com")
    ? [LEGACY_PARENT_DOMAIN]
    : [];
}

export function appendLegacyCookieExpirations(
  response,
  req,
  names,
  { paths = ["/"] } = {}
) {
  const baseOptions = getRequestCookieBaseOptions(req);
  let expirationCount = 0;
  getLegacyDomainsForRequest(req).forEach((domain) => {
    Array.from(new Set(names))
      .filter(isSerializableCookieName)
      .forEach((name) => {
        paths.forEach((path) => {
          const header = serializeCookieHeader(name, "", {
            ...baseOptions,
            domain,
            path,
            maxAge: 0,
            expires: new Date(0),
          });
          if (appendDistinctSetCookieHeader(response, header)) {
            expirationCount += 1;
          }
        });
      });
  });
  return expirationCount;
}

function getCookieMutationOptions(cookie, baseOptions) {
  const options = { ...cookie };
  delete options.name;
  delete options.value;
  delete options.domain;
  return {
    ...options,
    ...baseOptions,
  };
}

function replaceChangedSupabaseRoots(canonicalCookies, responseCookies) {
  const changedRoots = new Set(
    responseCookies.map(({ name }) => getSupabaseAuthCookieRoot(name)).filter(Boolean)
  );
  changedRoots.forEach((root) => {
    Array.from(canonicalCookies.keys()).forEach((name) => {
      if (getSupabaseAuthCookieRoot(name) === root) canonicalCookies.delete(name);
    });
  });

  responseCookies.forEach((cookie) => {
    if (!cookie?.name?.startsWith("sb-")) return;
    if (!cookie.value || cookie.maxAge === 0) {
      canonicalCookies.delete(cookie.name);
      return;
    }
    canonicalCookies.set(cookie.name, cookie);
  });
}

function getObsoleteHostOnlyAuthCookieNames(req, canonicalCookies) {
  const canonicalAuthNames = new Set(
    Array.from(canonicalCookies.keys()).filter(isSupabaseAuthCookieName)
  );
  const canonicalRoots = new Set(
    Array.from(canonicalAuthNames)
      .map(getSupabaseAuthCookieRoot)
      .filter(Boolean)
  );

  return getSbCookieNamesFromRequest(req).filter((name) => {
    const root = getSupabaseAuthCookieRoot(name);
    return (
      root &&
      canonicalRoots.has(root) &&
      !canonicalAuthNames.has(name)
    );
  });
}

/**
 * Final response-cookie operation for a validated session. Canonical cookies
 * are issued host-only first; legacy parent-domain expirations are appended
 * afterward as distinct raw Set-Cookie headers.
 */
export function finalizeSupabaseCookieScope(
  response,
  req,
  {
    validatedCookies,
    force = false,
    extraLegacyCookieNames = [],
    debug = false,
    log = console.info,
    route = "unknown",
  } = {}
) {
  const candidates = validatedCookies || [];
  const allResponseSupabaseCookies = response.cookies
    .getAll()
    .filter(({ name }) => name.startsWith("sb-"));
  const responseCookies = allResponseSupabaseCookies.filter(({ name }) =>
    isSupabaseAuthCookieName(name)
  );
  const canonicalCookies = new Map();
  candidates.forEach((cookie) => {
    if (isSupabaseAuthCookieName(cookie?.name) && cookie.value) {
      canonicalCookies.set(cookie.name, {
        name: cookie.name,
        value: cookie.value,
        maxAge: SUPABASE_COOKIE_MAX_AGE,
      });
    }
  });
  const hasExplicitValidatedSession =
    validatedCookies !== undefined && canonicalCookies.size > 0;
  const normalize =
    responseCookies.length > 0 ||
    (hasExplicitValidatedSession &&
      (force || shouldNormalizeSupabaseCookieScope(req)));
  if (!normalize) {
    const legacyExpirationCount = appendLegacyCookieExpirations(
      response,
      req,
      [
        ...allResponseSupabaseCookies.map(({ name }) => name),
        ...extraLegacyCookieNames,
      ]
    );
    return {
      attempted: false,
      canonicalCookieCount: 0,
      legacyExpirationCount,
    };
  }

  const baseOptions = getRequestCookieBaseOptions(req);
  replaceChangedSupabaseRoots(canonicalCookies, responseCookies);

  const obsoleteHostOnlyAuthCookieNames =
    getObsoleteHostOnlyAuthCookieNames(req, canonicalCookies);
  obsoleteHostOnlyAuthCookieNames.forEach((name) => {
    response.cookies.set(name, "", {
      ...baseOptions,
      maxAge: 0,
      expires: new Date(0),
    });
  });

  canonicalCookies.forEach((cookie) => {
    response.cookies.set(
      cookie.name,
      cookie.value,
      getCookieMutationOptions(cookie, baseOptions)
    );
  });

  const obsoleteLegacyCookieNames =
    getKnownSupabaseCookieNamesFromRequest(req).filter(
      isLegacySupabaseCookieName
    );
  const obsoleteLegacyCookieExpirationCount = clearCookieScopes(
    response,
    req,
    obsoleteLegacyCookieNames,
    { paths: ["/", "/admin"] }
  );

  const hasCanonicalSession = Array.from(canonicalCookies.keys()).some(
    isSupabaseAuthCookieName
  );
  response.cookies.set(AUTH_COOKIE_SCOPE_MARKER, hasCanonicalSession ? AUTH_COOKIE_SCOPE_VERSION : "", {
    ...baseOptions,
    httpOnly: true,
    maxAge: hasCanonicalSession ? SUPABASE_COOKIE_MAX_AGE : 0,
    ...(!hasCanonicalSession ? { expires: new Date(0) } : {}),
  });

  const namesToExpire = Array.from(
    new Set([
      ...getSupabaseAuthCookieNamesFromRequest(req),
      ...allResponseSupabaseCookies.map(({ name }) => name),
      ...canonicalCookies.keys(),
      ...extraLegacyCookieNames,
      AUTH_COOKIE_SCOPE_MARKER,
    ])
  ).filter(isSerializableCookieName);
  const legacyExpirationCount = appendLegacyCookieExpirations(
    response,
    req,
    namesToExpire
  );

  if (debug && typeof log === "function") {
    log("[AUTH_COOKIE_SCOPE]", {
      route,
      scope: AUTH_COOKIE_SCOPE,
      duplicateNames: findDuplicateSbCookieNames(req),
      candidateCount: getSupabaseCookieCandidates(req).length,
      canonicalCookieNames: Array.from(canonicalCookies.keys()),
      canonicalCookieCount: canonicalCookies.size,
      obsoleteHostOnlyCookieNames: obsoleteHostOnlyAuthCookieNames,
      obsoleteLegacyCookieNames,
      legacyExpirationCount:
        legacyExpirationCount + obsoleteLegacyCookieExpirationCount,
      success: hasCanonicalSession,
    });
  }

  return {
    attempted: true,
    canonicalCookieCount: canonicalCookies.size,
    legacyExpirationCount:
      legacyExpirationCount + obsoleteLegacyCookieExpirationCount,
  };
}

/**
 * Clears host-only canonical cookies and the legacy parent-domain cookies with
 * separate raw headers. Call this after every other response.cookies.set.
 */
export function clearCookieScopes(response, req, names, { paths = ["/"] } = {}) {
  const baseOptions = getRequestCookieBaseOptions(req);
  const domains = [undefined, ...getLegacyDomainsForRequest(req)];
  let expirationCount = 0;

  Array.from(new Set(names))
    .filter(isSerializableCookieName)
    .forEach((name) => {
      paths.forEach((path) => {
        domains.forEach((domain) => {
          const header = serializeCookieHeader(name, "", {
            ...baseOptions,
            path,
            ...(domain ? { domain } : {}),
            maxAge: 0,
            expires: new Date(0),
          });
          if (appendDistinctSetCookieHeader(response, header)) {
            expirationCount += 1;
          }
        });
      });
    });

  return expirationCount;
}

export function clearSupabaseCookies(res, req, opts = {}) {
  const { log, debug, extraNames = [] } = opts;
  const expectedCookieName = getSupabaseAuthCookieName();
  const names = Array.from(
    new Set([
      ...getSbCookieNamesFromRequest(req),
      ...getKnownSupabaseCookieNamesFromRequest(req),
      ...(expectedCookieName ? [expectedCookieName] : []),
      ...(expectedCookieName ? [`__Host-${expectedCookieName}`] : []),
      ...LEGACY_SUPABASE_COOKIE_NAMES,
      AUTH_COOKIE_SCOPE_MARKER,
      ...extraNames,
    ])
  );
  const expirationCount = clearCookieScopes(res, req, names, {
    paths: ["/", "/admin"],
  });

  if (debug && typeof log === "function") {
    log("cleared auth cookie scopes", {
      names,
      scopes: [
        "(host-only)",
        ...(getLegacyDomainsForRequest(req).length ? [LEGACY_PARENT_DOMAIN] : []),
      ],
      expirationCount,
    });
  }

  return names;
}

export function copySetCookieHeaders(source, target) {
  getSetCookieHeaders(source).forEach((header) => {
    appendDistinctSetCookieHeader(target, header);
  });
  return target;
}

function isCookieDeletionHeader(header) {
  const value = String(header || "");
  return (
    /^[^=;]+=\s*(?:;|$)/.test(value) ||
    /(?:^|;)\s*Max-Age\s*=\s*0(?:;|$)/i.test(value) ||
    /(?:^|;)\s*Expires\s*=\s*Thu,\s*01\s+Jan\s+1970\b/i.test(value)
  );
}

export function copyNonClearingSetCookieHeaders(source, target) {
  const headers = getSetCookieHeaders(source);
  const rootsWithWrites = new Set(
    headers
      .filter((header) => !isCookieDeletionHeader(header))
      .map((header) => {
        const name = String(header).match(/^([^=;]+)=/)?.[1] || "";
        return getSupabaseAuthCookieRoot(name);
      })
      .filter(Boolean)
  );
  headers.forEach((header) => {
    if (isCookieDeletionHeader(header)) {
      const name = String(header).match(/^([^=;]+)=/)?.[1] || "";
      const root = getSupabaseAuthCookieRoot(name);
      if (!root || !rootsWithWrites.has(root)) return;
    }
    appendDistinctSetCookieHeader(target, header);
  });
  return target;
}

export function createBrowserSupabaseCookieMethods() {
  return {
    getAll() {
      if (typeof document === "undefined") return [];
      const entries = parseCookieHeaderEntries(document.cookie);
      const browserRequest = {
        headers: {
          get(name) {
            return String(name || "").toLowerCase() === "cookie"
              ? document.cookie
              : null;
          },
        },
      };
      const ambiguousAuthRoots = new Set(
        getSupabaseCookieCandidates(browserRequest).length > 1
          ? entries
              .map(({ name }) => getSupabaseAuthCookieRoot(name))
              .filter(Boolean)
          : []
      );
      return entries
        .filter(({ name }) => {
          const root = getSupabaseAuthCookieRoot(name);
          return !root || !ambiguousAuthRoots.has(root);
        })
        .map(({ name, value }) => ({
          name,
          value,
        }));
    },
    setAll(cookiesToSet) {
      if (typeof document === "undefined") return;
      const baseOptions = getBrowserCookieBaseOptions();
      const existingEntries = parseCookieHeaderEntries(document.cookie);
      const authCookieChanges = cookiesToSet.filter(({ name }) =>
        isSupabaseAuthCookieName(name)
      );
      const changedAuthRoots = new Set(
        authCookieChanges
          .map(({ name }) => getSupabaseAuthCookieRoot(name))
          .filter(Boolean)
      );
      const nextAuthNames = new Set(
        authCookieChanges
          .filter(({ value, options }) => Boolean(value) && options?.maxAge !== 0)
          .map(({ name }) => name)
      );
      const obsoleteAuthNames = Array.from(
        new Set(
          existingEntries
            .filter(({ name }) => {
              const root = getSupabaseAuthCookieRoot(name);
              return (
                root &&
                changedAuthRoots.has(root) &&
                !nextAuthNames.has(name)
              );
            })
            .map(({ name }) => name)
        )
      );
      const obsoleteLegacyNames = Array.from(
        new Set(
          existingEntries
            .filter(({ name }) => isLegacySupabaseCookieName(name))
            .map(({ name }) => name)
        )
      );

      // Supabase reuses the same cookie name when MFA promotes a session from
      // AAL1 to AAL2. Clear the historical /admin-scoped copy even when its
      // name is still canonical, otherwise /admin receives both generations
      // while /auth only sees the new root-scoped session.
      authCookieChanges.forEach(({ name }) => {
        document.cookie = serializeCookieHeader(name, "", {
          ...baseOptions,
          path: "/admin",
          maxAge: 0,
          expires: new Date(0),
        });
      });

      [...obsoleteAuthNames, ...obsoleteLegacyNames].forEach((name) => {
        ["/", "/admin"].forEach((path) => {
          document.cookie = serializeCookieHeader(name, "", {
            ...baseOptions,
            path,
            maxAge: 0,
            expires: new Date(0),
          });
        });
      });

      cookiesToSet.forEach(({ name, value, options }) => {
        const canonicalOptions = {
          ...options,
          ...baseOptions,
        };
        delete canonicalOptions.domain;
        document.cookie = serializeCookieHeader(name, value, canonicalOptions);
      });

      if (
        getCookieHostname(window.location.host).endsWith(".yourbarrio.com")
      ) {
        const parentCleanupNames = new Set([
          ...cookiesToSet.map(({ name }) => name),
          ...obsoleteAuthNames,
          ...obsoleteLegacyNames,
        ]);
        parentCleanupNames.forEach((name) => {
          ["/", "/admin"].forEach((path) => {
            document.cookie = serializeCookieHeader(name, "", {
              ...baseOptions,
              domain: LEGACY_PARENT_DOMAIN,
              path,
              maxAge: 0,
              expires: new Date(0),
            });
          });
        });
      }

      if (authCookieChanges.length) {
        const hasSessionCookie = authCookieChanges.some(
          ({ value, options }) => Boolean(value) && options?.maxAge !== 0
        );
        document.cookie = serializeCookieHeader(
          AUTH_COOKIE_SCOPE_MARKER,
          hasSessionCookie ? AUTH_COOKIE_SCOPE_VERSION : "",
          {
            ...baseOptions,
            httpOnly: false,
            maxAge: hasSessionCookie ? SUPABASE_COOKIE_MAX_AGE : 0,
            ...(!hasSessionCookie ? { expires: new Date(0) } : {}),
          }
        );
        if (
          getCookieHostname(window.location.host).endsWith(".yourbarrio.com")
        ) {
          document.cookie = serializeCookieHeader(AUTH_COOKIE_SCOPE_MARKER, "", {
            ...baseOptions,
            domain: LEGACY_PARENT_DOMAIN,
            maxAge: 0,
            expires: new Date(0),
          });
        }
      }
    },
  };
}

export function logSupabaseCookieDiagnostics({ req, debug, log }) {
  if (!debug || typeof log !== "function") return;
  const names = getSbCookieNamesFromRequest(req);
  const duplicates = findDuplicateSbCookieNames(req);
  log("sb cookie snapshot", {
    names,
    duplicates,
    scope: AUTH_COOKIE_SCOPE,
    candidateCount: getSupabaseCookieCandidates(req).length,
  });
}
